import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterLink } from '@angular/router';
import { AppSwitcherComponent, CopadoAppId, IconComponent } from '@copado/cds';
import { SearchInputComponent } from '@copado/cds';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime } from 'rxjs/operators';

import { AppRouteData } from '@app/app-routing.interface';
import { UserState } from '@app/auth/user.state';
import { BootstrapSuffix } from '@app/common.model';
import { LayoutService } from '@app/layout/layout.service';
import { NavItem, NavItemComponent } from '@app/layout/sidebar/nav-item/nav-item.component';
import { OrgState, Status as OrgStatus, ProjectWithStar } from '@app/orgs/org.state';
import { ProjectDetailTab } from '@app/projects/project/project.model';
import { ProjectState, Status as ProjectStatus } from '@app/projects/project.state';
import { PROJECT_ID_PARAM } from '@app/shared/app-router.service';
import { CanUseService, Feature } from '@app/shared/can-use.service';
import { Modal } from '@app/shared/modal/modal.model';
import { Offcanvas } from '@app/shared/offcanvas/offcanvas.model';
import { getInitials } from '@app/shared/string.service';
import { SwitcherComponent } from '@app/shared/switcher/switcher.component';
import { ToastService } from '@app/toasts/toast.service';

export type NavItemType =
  | 'home'
  | 'jobs'
  | 'runs'
  | 'robots'
  | 'explorer'
  | 'settings'
  | 'dataTables';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AppSwitcherComponent,
    IconComponent,
    NavItemComponent,
    NgClass,
    NgbTooltip,
    ReactiveFormsModule,
    RouterLink,
    SearchInputComponent,
    SwitcherComponent,
  ],
  selector: 'app-sidebar',
  styleUrls: ['./sidebar.component.scss'],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent {
  protected readonly Feature = Feature;
  protected readonly Offcanvas = Offcanvas;
  protected readonly ProjectDetailTab = ProjectDetailTab;
  protected readonly Modal = Modal;
  protected readonly CopadoAppId = CopadoAppId;

  private readonly canUseService = inject(CanUseService);
  protected readonly layout = inject(LayoutService);
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected readonly projectState = inject(ProjectState);
  protected readonly toastService = inject(ToastService);
  protected readonly orgState = inject(OrgState);
  protected readonly userState = inject(UserState);

  protected search = new FormControl('', { nonNullable: true });
  protected projectSwitcherOpen = signal(false);
  private searchTerm = toSignal(this.search.valueChanges.pipe(debounceTime(300)));

  protected readonly initials = computed(() =>
    getInitials(this.projectState.project()?.name ?? '', 3),
  );

  protected readonly orgName = computed(() => this.userState.org()?.name);

  protected filteredProjects = computed(() => {
    const term = (this.searchTerm() ?? '').toLocaleLowerCase();
    const projects = this.orgState.projects();

    return term ? projects.filter((r) => this.searchBy(r, term.toLocaleLowerCase())) : projects;
  });

  protected readonly canUseAppSwitcher = computed(() =>
    this.canUseService.can(Feature.AppSwitcher),
  );

  protected readonly canUseDataTables = computed(() => this.canUseService.can(Feature.DataTables));

  private readonly queryParams = toSignal(this.route.queryParams);

  protected readonly navItems = computed<Record<NavItemType, NavItem>>(() => {
    const defaultQueryParams = {
      projectId: '' + this.projectState.projectId(),
      orgId: '' + this.userState.orgId(),
    };

    return {
      dataTables: {
        icon: 'DevelopmentDatabase',
        id: 'nav-data-tables-link',
        label: $localize`Data Tables`,
        queryParams: defaultQueryParams,
        routerLink: '/data-tables',
        routerOptions: { exact: false },
      },
      explorer: {
        icon: 'CommerceBinoculars',
        id: 'nav-explorer-link',
        label: $localize`Copado Explorer`,
        queryParams: defaultQueryParams,
        routerLink: '/explorations',
        routerOptions: { exact: false },
      },
      home: {
        icon: 'MapsAndTravelHouse',
        id: 'nav-home-link',
        label: $localize`Project Overview`,
        queryParams: defaultQueryParams,
        routerLink: '/',
        routerOptions: {
          fragment: 'exact',
          matrixParams: 'exact',
          paths: 'exact',
          queryParams: 'ignored',
        },
      },
      jobs: {
        icon: 'CRTPlans',
        id: 'nav-jobs-link',
        label: $localize`Test Jobs`,
        queryParams: defaultQueryParams,
        routerLink: '/jobs',
        routerOptions: { exact: false },
      },
      robots: {
        icon: 'DevelopmentRobot',
        id: 'nav-robots-link',
        label: $localize`Robots`,
        queryParams: defaultQueryParams,
        routerLink: '/robots',
        routerOptions: { exact: false },
      },
      runs: {
        icon: 'CRTRuns',
        id: 'nav-jobs-link',
        label: $localize`Test Runs`,
        queryParams: defaultQueryParams,
        routerLink: '/runs',
        routerOptions: { exact: false },
      },
      settings: {
        icon: 'SystemAndDevicesGearSix',
        id: 'nav-project-details-link',
        label: $localize`Project Settings`,
        queryParams: {
          ...defaultQueryParams,
          offcanvas: Offcanvas.ProjectDetails,
          view: ProjectDetailTab.Config,
        },
        routerLink: '',
        routerOptions: { exact: true },
      },
    };
  });

  private searchBy(project: ProjectWithStar, term: string): boolean {
    return project.name.toLocaleLowerCase().includes(term);
  }

  open(open: boolean): void {
    if (!open) {
      this.search.setValue('');
    }
    this.projectSwitcherOpen.set(open);
  }

  switchToProject(project: number | ProjectWithStar): void {
    // List of known query params that should persist when switching projects
    const PERSIST_PARAMS = [
      't', // Query param key for devops exchange links
    ];

    const q = this.queryParams();
    const persistParams = q && Object.keys(q).filter((key) => PERSIST_PARAMS.includes(key));

    const projectId = typeof project === 'number' ? project : project.id;
    const urlPaths = this.getProjectUrl(this.route.snapshot.root);

    this.router.navigate(urlPaths, {
      queryParams: { [PROJECT_ID_PARAM]: projectId },
      queryParamsHandling: persistParams ? 'merge' : '',
    });
    // In some cases, the project state may not update after navigation (e.g. the profile page).
    // Ensure the project state is correctly set to the desired project.
    this.projectState.loadProject$.next('' + projectId);
  }

  private getProjectUrl(snapshot: ActivatedRouteSnapshot): string[] {
    if ((snapshot.data as AppRouteData).projectChangeUrl) {
      return snapshot.data.projectChangeUrl;
    }
    if (snapshot.firstChild) {
      return this.getProjectUrl(snapshot.firstChild);
    }
    return [];
  }

  constructor() {
    effect(() => {
      const orgId = this.userState.orgId();
      const projects = this.orgState.projects();
      const orgStatus = this.orgState.status();
      const projectStatus = this.projectState.status();

      untracked(async () => {
        if (
          orgId &&
          orgStatus === OrgStatus.Ready &&
          projectStatus !== ProjectStatus.Loading &&
          !this.projectState.project()
        ) {
          const defaultProject = this.projectState.getDefaultProject(orgId);
          const user = this.userState.user();
          if (projects[0]) {
            if (defaultProject && projects.some((p) => p.id === defaultProject)) {
              this.switchToProject(defaultProject);
            } else {
              this.switchToProject(projects[0]);
            }
          } else if (
            projectStatus === ProjectStatus.Ready &&
            this.canUseService.can(Feature.EditProject) &&
            !user?.superAdmin
          ) {
            await this.projectState.createProject({
              description: $localize`This project was automatically created.`,
              name: $localize`${user?.name}'s Project`,
            });
            this.toastService.displayToast(
              BootstrapSuffix.Success,
              $localize`A new default project has been successfully created for ${user?.name}.`,
              $localize`Project Created Successfully`,
            );
          }
        }
      });
    });
  }
}
